import { Eye, MoreHorizontal, Pencil, Power, PowerOff, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCountryScope, usePermissions } from '@/features/auth';

import type { BrandListItem } from '../types/brand.types';

/**
 * Per-row actions.
 *
 * Collapsed into a menu rather than a row of icon buttons, so that a
 * destructive-ish action (deactivating a brand pulls it from the storefront)
 * cannot be hit by a mis-aimed click while scanning the table (§68).
 *
 * Each item is permission-gated individually: a user with catalogue.read but
 * not catalogue.manage sees a menu with one item rather than three
 * that error. A brand exists in every country, so editing it or changing its
 * status also needs all-country scope — the Admin API refuses it otherwise.
 */
export function BrandRowActions({
  brand,
  onEdit,
  onToggleActive,
  onDelete,
}: {
  brand: BrandListItem;
  onEdit: (brand: BrandListItem) => void;
  onToggleActive: (brand: BrandListItem) => void;
  /** Opens a dialog that checks whether anything uses the brand before offering to delete it. */
  onDelete: (brand: BrandListItem) => void;
}) {
  const { can } = usePermissions();
  const { allCountries } = useCountryScope();
  const canUpdate = can('catalogue.manage') && allCountries;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          aria-label={`Actions for ${brand.name}`}
        >
          <MoreHorizontal className="size-3.5" aria-hidden />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem asChild className="gap-2 text-xs">
          <Link to={`/brands/${brand.id}`}>
            <Eye className="size-3.5" aria-hidden />
            View details
          </Link>
        </DropdownMenuItem>

        {canUpdate && (
          <>
            <DropdownMenuItem onSelect={() => onEdit(brand)} className="gap-2 text-xs">
              <Pencil className="size-3.5" aria-hidden />
              Edit brand
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onSelect={() => onToggleActive(brand)}
              className="gap-2 text-xs"
              variant={brand.isActive ? 'destructive' : 'default'}
            >
              {brand.isActive ? (
                <>
                  <PowerOff className="size-3.5" aria-hidden />
                  Deactivate
                </>
              ) : (
                <>
                  <Power className="size-3.5" aria-hidden />
                  Activate
                </>
              )}
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onSelect={() => onDelete(brand)}
              className="gap-2 text-xs"
              variant="destructive"
            >
              <Trash2 className="size-3.5" aria-hidden />
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
